/**
 * The traffic pilot (M4 spec §10.3, plan I4): flies the real traffic ships (`src/traffic/realShips.ts`) through the
 * same flight model as every pilot, writing `Control` only (D-013). It runs on M2's `control` sim stage
 * (`registerSimStage(sim, 'control', stage)`), after M1's `stepAIPilots`, and skips ships that have an `AIPilot`.
 *
 * Phases (`TRAFFIC_PHASE`):
 * - `undock`: out along the leg-start station's bay out axis at `undockSpeed` until `undockClearM` off its hull
 *   (R + `undockClearM` from the centre, R its bounding radius; traffic-pilot.json), then `sublight`. With no bay
 *   known, straight out from the leg's start until `dockStartM` clear.
 * - `sublight`: toward the step's target at `sublightFraction × maxSpeed` with M1's obstacle avoidance. On a leg,
 *   clear of both ends (more than `cruiseClearM` from the start and `approachDropM` from the target) it hands the
 *   ship back to cruise (`cruise`: the tier step demotes it to a kinematic contact). Near a station target it
 *   docks; near any other target the leg has arrived (`arrived`).
 * - `dock`: M2's `stepAutodock` with the station's `BayFrame` (seam S-M2-5) from `dockStartM` off the hull (R +
 *   `dockStartM` from the centre; scale SC-5d2); `arrived` → `done`
 *   (inside the station: the tier step despawns it and the step reports). `failed` retries once from the entry,
 *   then gives up (`done`, logged). Without a bay provider the ship is `done` on reaching `dockStartM` (logged once).
 * - `mine`: holds `mineStandoffM` off the field node, nose on it, with `ToolControl` mining at `minePower`
 *   (seam S-M2-10) so M2's tool systems draw the beam.
 * - `hold`: orbits the node at `holdOrbitM`, off the hull for a station node (R + `holdOrbitM`; patrols, lurking raiders);
 *   a shown stand-by ship (RR-F1 BL-1b) holds still at its point.
 *
 * Moor-band hulls (M10 P1e3: `bands.ts dockModeOf(def.sizeBand) === "moor"`, an L) never enter a bay. Their `dock`
 * phase flies to an AI mooring-ring slot (`docking/moor.ts moorRingPoint`, never the player's `moor.json playerSlot`)
 * round the station from `bays.bayOf`, holds there `moor.json aiDwellS`, and becomes `done` only once the ship is out
 * of the player's view (outside `despawnViewHalfAngleDeg` of the player's nose, or beyond `despawnFarM`), so it
 * never vanishes in plain sight. A moor-band ship promoted on a leg from a station spawns at its ring slot
 * (`moorSpawnPoint`, called by `realShips.ts promote`) and its `undock` phase runs radially out until clear of the
 * ring; while that station's bay isn't ready yet it stays a contact (`MOOR_WAIT`, M10 P3 W4b), never spawning in the
 * station on top of the ships undocking with it. S and M hulls dock through the bay exactly as before.
 *
 * Threat: a hit, or a hostile (`isHostile`) within `threatM`, adds an M1 `AIPilot` with the role's profile
 * (local.json `aiProfiles`); with no hostile within `calmM` for `calmS` it is removed and the traffic pilot resumes.
 * The M1 pilot code is unchanged.
 *
 * Per tick: allocation-free (module scratch, index loops; D-014). Float64 world positions (D-011).
 */
import { addComponent, hasComponent, removeComponent } from 'bitecs'
import { z } from 'zod'
import { isHostile } from '../combat/hostility'
import { logger } from '../core/logger'
import { clamp, inverseRotateVec, QuatScratch, rotateVec, Vec3Scratch, type Quat, type Vec3 } from '../core/math'
import { ControlScratch, createAutodockState, resetAutodock, stepAutodockAt, type AutodockConfig, type AutodockShip, type AutodockState } from '../docking/autopilot'
import { bandRank, dockModeOf } from '../data/bands'
import type { ShipDef } from '../data/schemas'
import { moorRingPoint, moorRingRadius, moorRingTangent } from '../docking/moor'
import { moorConfig, type MoorConfig } from '../docking/moorConfig'
import type { ControlInput } from '../flight/flightModel'
import type { Sim, SimStageFn } from '../sim'
import type { BayFrame } from '../space/contracts'
import { TOOL } from '../space/contracts'
import { ToolControl } from '../space/components'
import localJson from '../data/strategic/local.json'
import { localConfigSchema, type LocalConfig } from '../strategic/schemas'
import { TrafficPilot, TRAFFIC_PHASE } from '../traffic/components'
import { STEP_KIND, TARGET_KIND, type ContactStore } from '../traffic/contacts'
import { AIPilot, Control, Hull, Life, MAX_ENTITIES, Pos64, Rot, Shield, Ship, Team, Vel } from '../world/components'
import { AvoidArgs, avoidObstacles } from './avoidance'
import { setPilotBand } from './pilot'
import { SteerArgs, steerTo } from './steering'
import trafficPilotJson from '../data/traffic-pilot.json'

/** The traffic tuning the pilot reads (local.json). */
export type TrafficPilotConfig = Pick<
  LocalConfig,
  | 'undockSpeed'
  | 'sublightFraction'
  | 'cruiseClearM'
  | 'approachDropM'
  | 'dockStartM'
  | 'mineStandoffM'
  | 'minePower'
  | 'holdOrbitM'
  | 'threatM'
  | 'calmM'
  | 'calmS'
  | 'aiProfiles'
>

/** A station's docking bay for the AI (seam S-M2-5): writes the frame and the station bounds; false when unknown. */
export interface TrafficBays {
  bayOf(stationId: string, out: TrafficBay): boolean
  /**
   * Writes the bay of the station whose bounds contain world point (x, y, z) (a leg's `from` at a station is its
   * centre); false when none. Optional: without it a moor-band ship promoted on a leg spawns where its contact is
   * (M10 P1e3; rare, at promotion only).
   */
  bayAt?(x: number, y: number, z: number, out: TrafficBay): boolean
  /**
   * True when a station (its model attached or not) has its centre within `within` m of world point (x, y, z).
   * Optional: with it, a moor-band ship whose leg starts at a station with no bay yet waits as a contact instead of
   * spawning in the station (M10 P3 W4b; rare, at promotion only). With `out`, writes that station's centre and
   * bounding radius (from its manifest entry, known before the model attaches) to `out.pos` and `out.radius`.
   */
  stationNear?(x: number, y: number, z: number, within: number, out?: TrafficBay): boolean
}
export interface TrafficBay {
  frame: BayFrame | null
  pos: Vec3
  radius: number
}

export interface TrafficPilotEnv {
  store: ContactStore
  cfg: TrafficPilotConfig
  /** M2's docking tuning (docking.json); null → docking completes at `dockStartM`. */
  docking: AutodockConfig | null
  /** The bay provider; null → docking completes at `dockStartM`. */
  bays: TrafficBays | null
  /** AI profile index by id (M1 ai-profiles.json). */
  profileIndex(id: string): number
  /** Called when a real ship's leg arrived at a non-station target (the step reporter). */
  arrived(slot: number): void
  /** The mooring tuning for moor-band hulls (M10 P1e3); omitted → `moor.json`. */
  moor?: MoorConfig
}

const PILOT_TUNING = localConfigSchema.parse(localJson)
/** Speed per metre of remaining distance when closing on a point (1/s): the braking profile of every phase (local.json). */
const ARRIVE_GAIN = PILOT_TUNING.arriveGain
/** Arrival radius at a non-station leg target, as a fraction of the hold orbit (local.json). */
const ARRIVE_FRACTION = PILOT_TUNING.arriveFraction
/** How far ahead on the hold orbit the pilot steers, radians (local.json). */
const ORBIT_LEAD = PILOT_TUNING.orbitLead
const TRAFFIC_PILOT = z
  .strictObject({ comment: z.string(), undockClearM: z.number().positive(), wreckClearM: z.number().nonnegative() })
  .parse(trafficPilotJson)
/** How far off the hull an S/M ship's bay-axis undock runs (m; traffic-pilot.json, scale SC-5d2). */
const UNDOCK_CLEAR_M = TRAFFIC_PILOT.undockClearM
/** How far off a station's hull an off-screen wreck or hulk materialises at least (m; traffic-pilot.json, scale SC-5e1). */
export const WRECK_CLEAR_M = TRAFFIC_PILOT.wreckClearM
/** moor.json, for envs that pass no `moor` (M10 P1e3). */
const MOOR_TUNING = moorConfig()
const DEG = Math.PI / 180

/**
 * The ship rotation scratch `flyTo` fills every tick. A class, not a `{ x, y, z, w }` literal: every literal whose
 * first key is `x` shares V8's map tree, and once anything stores a non-number there the `x` field is tagged
 * and each float store into it boxes a heap number (gate-A decision).
 */
class RotScratch implements Quat {
  x = 0
  y = 0
  z = 0
  w = 1
}

// Scratch (D-014).
const local: Vec3 = { x: 0, y: 0, z: 0 }
const rot: Quat = new RotScratch()
/**
 * The bay scratch, a class (own map, number-initialised `radius`): `stepDock` passes it to `stepAutodockAt` as the
 * station, so the radius crosses no call as a float (DA-1 follow-up, D-TBD; the `stepAutodock` wrapper boxed it).
 */
class TrafficBayScratch implements TrafficBay {
  frame: BayFrame | null = null
  pos: Vec3 = new Vec3Scratch()
  radius = 0
}
const bay = new TrafficBayScratch()
// The dock step's ship and stick: classes, not literals (DA-1 follow-up, D-TBD; D-1052): a literal shares its map with
// every literal of that shape game-wide, so a field can turn tagged and each float store into it or load in the
// autopilot boxes (the traffic dock phase read 112 B/tick through them).
const shipPos: Vec3 = new Vec3Scratch()
const shipVel: Vec3 = new Vec3Scratch()
const shipRot: Quat = new QuatScratch()
const dockShip: AutodockShip = { pos: shipPos, vel: shipVel, rot: shipRot }
const dockOut: ControlInput = new ControlScratch()
const ring: Vec3 = { x: 0, y: 0, z: 0 }
const nose: Vec3 = { x: 0, y: 0, z: 0 }
const spawnBay: TrafficBay = { frame: null, pos: { x: 0, y: 0, z: 0 }, radius: 0 }

/** Per-entity docking state and bookkeeping, grown on demand at promotion (rare). */
const dockStates: (AutodockState | undefined)[] = []
const dockRetries = new Uint8Array(MAX_ENTITIES)
const lastHealth = new Float32Array(MAX_ENTITIES)
let warnedNoBays = false
let warnedNoRing = false
/** Moor-band ships (M10 P1e3): the AI ring slot, the seconds held at it, and the undock clear distance (m; 0 → `dockStartM`). */
const moorSlot = new Uint8Array(MAX_ENTITIES)
const moorHeld = new Float32Array(MAX_ENTITIES)
const undockClear = new Float32Array(MAX_ENTITIES)
/**
 * Per ship, stride 3 (scale SC-5d2): the point `undockClear` is measured from (the leg-start station's centre, else the
 * leg's start), the bay out axis the undock follows (zero → radially out from that point) and the bay mouth on it.
 */
const undockFrom = new Float64Array(MAX_ENTITIES * 3)
const undockAxis = new Float64Array(MAX_ENTITIES * 3)
const undockMouth = new Float64Array(MAX_ENTITIES * 3)
/** The target station's bounding radius (m; 0 for other nodes or while its bay is unknown): dock and hold ranges are from its hull. */
const targetR = new Float32Array(MAX_ENTITIES)
/** The stage step (s), set by `createTrafficPilotStage`: a module value, since a float64 argument is boxed per call. */
let stageDt = 1 / 60

/** Zeroes the ship's controls and tools. */
function idle(eid: number): void {
  Control.surge[eid] = Control.sway[eid] = Control.heave[eid] = 0
  Control.pitch[eid] = Control.yaw[eid] = Control.roll[eid] = 0
  Control.boost[eid] = Control.firePrimary[eid] = Control.fireSecondary[eid] = 0
}

/**
 * `flyTo`'s target and result. `flyTo` is over V8's inlining limit, and floats passed to or returned from a call
 * that is not inlined are boxed per call, so they go through this scratch (a class, so its float fields stay
 * unboxed; gate-A decision).
 */
export class FlyTarget {
  /** World point (m, float64). */
  tx = 0
  ty = 0
  tz = 0
  /** Speed cap (m/s). */
  cap = 0
  /** Result: the distance to the point (m). */
  dist = 0
}

const fly = new FlyTarget()
const steerArgs = new SteerArgs()
const avoidArgs = new AvoidArgs()

/** Fills `fly` (small enough to inline at every caller). */
function aim(x: number, y: number, z: number, cap: number): FlyTarget {
  fly.tx = x
  fly.ty = y
  fly.tz = z
  fly.cap = cap
  return fly
}

/**
 * Flies `eid` toward `t.tx, t.ty, t.tz`, braking as `ARRIVE_GAIN × distance`, capped at `t.cap` m/s (flight assist:
 * the stick is the desired local velocity over `maxSpeed`), nose along the travel. Writes the distance to `t.dist`.
 */
export function flyTo(sim: Sim, eid: number, def: ShipDef, t: FlyTarget): void {
  const cap = t.cap
  const dx = t.tx - Pos64.x[eid]
  const dy = t.ty - Pos64.y[eid]
  const dz = t.tz - Pos64.z[eid]
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
  const speed = dist * ARRIVE_GAIN < cap ? dist * ARRIVE_GAIN : cap
  const inv = dist > 1e-6 ? speed / dist : 0
  rot.x = Rot.x[eid]
  rot.y = Rot.y[eid]
  rot.z = Rot.z[eid]
  rot.w = Rot.w[eid]
  inverseRotateVec(rot, dx * inv, dy * inv, dz * inv, local)
  const m = def.maxSpeed
  Control.surge[eid] = clamp(-local.z / m, -1, 1)
  Control.sway[eid] = clamp(local.x / m, -1, 1)
  Control.heave[eid] = clamp(local.y / m, -1, 1)
  Control.boost[eid] = 0
  Control.firePrimary[eid] = Control.fireSecondary[eid] = 0
  const steer = sim.data.aiProfiles.tuning.steering
  if (dist > 1) steerDir(dx, dy, dz, steer.turnGain, steer.rollGain, eid)
  else Control.pitch[eid] = Control.yaw[eid] = Control.roll[eid] = 0
  if (speed > 1) {
    const a = avoidArgs
    a.ignoreEid = -1
    a.radius = def.colliderRadius
    a.feeler = eid
    avoidObstacles(eid, sim.raycast, sim.data.aiProfiles.tuning, a)
  }
  t.dist = dist
}

/** `steerTo` through the module `SteerArgs` (small enough to inline). */
function steerDir(dx: number, dy: number, dz: number, turnGain: number, rollGain: number, eid: number): void {
  const a = steerArgs
  a.dx = dx
  a.dy = dy
  a.dz = dz
  a.turnGain = turnGain
  a.rollGain = rollGain
  steerTo(eid, a)
}

function distTo(eid: number, x: number, y: number, z: number): number {
  const dx = x - Pos64.x[eid]
  const dy = y - Pos64.y[eid]
  const dz = z - Pos64.z[eid]
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

/**
 * Starts the traffic pilot for a ship just promoted at `slot` (or given a new step): picks the phase from the
 * step and the ship's place (spec §10.3). `undockM` is how far from the leg-start station's centre the `undock`
 * phase runs, radially out (a moor-band ship spawned at its ring slot passes `moorSpawnPoint`'s result); omitted,
 * the ship undocks along the bay's out axis to `undockClearM` off the hull, or, with no bay known at the leg's start,
 * `dockStartM` from it (scale SC-5d2). Rare (promotion, new step).
 */
export function beginTrafficPilot(env: TrafficPilotEnv, eid: number, slot: number, undockM = -1): void {
  const s = env.store
  const cfg = env.cfg
  TrafficPilot.timer[eid] = 0
  ToolControl.fire[eid] = 0
  dockRetries[eid] = 0
  moorHeld[eid] = 0
  targetR[eid] = env.bays !== null && env.bays.bayOf(s.targetId[slot], bay) ? bay.radius : 0
  lastHealth[eid] = Hull.value[eid] + Shield.value[eid]
  const kind = s.kind[slot]
  // SC-5d3: holding at a station whose bay isn't attached yet: R from its manifest entry, the orbit its contact sat on.
  if (targetR[eid] === 0 && kind === STEP_KIND.holding && env.bays?.stationNear?.(s.tx[slot], s.ty[slot], s.tz[slot], 1, bay) === true) targetR[eid] = bay.radius
  let phase: number = TRAFFIC_PHASE.sublight
  if (kind === STEP_KIND.mining) phase = TRAFFIC_PHASE.mine
  else if (kind === STEP_KIND.holding) phase = TRAFFIC_PHASE.hold
  else if (kind === STEP_KIND.leg) {
    const clear = beginUndock(env, eid, slot, undockM)
    const o = eid * 3
    if (distTo(eid, undockFrom[o], undockFrom[o + 1], undockFrom[o + 2]) < clear) phase = TRAFFIC_PHASE.undock
  }
  TrafficPilot.phase[eid] = phase
  TrafficPilot.tx[eid] = s.tx[slot]
  TrafficPilot.ty[eid] = s.ty[slot]
  TrafficPilot.tz[eid] = s.tz[slot]
  TrafficPilot.speedCap[eid] = phase === TRAFFIC_PHASE.undock ? cfg.undockSpeed : 0
}

/**
 * Sets ship `eid`'s undock frame for its leg at `slot` and returns the clear distance (scale SC-5d2). A leg starting at
 * a station with a bay (`bayAt` on the leg's start, its centre or its bay mouth): measured from the station centre, and
 * an S/M ship follows the bay's out axis to R + `undockClearM`. Otherwise from the leg's start, `dockStartM`. Rare.
 */
function beginUndock(env: TrafficPilotEnv, eid: number, slot: number, undockM: number): number {
  const s = env.store
  const o = eid * 3
  const bays = env.bays
  const frame = bays !== null && bays.bayAt !== undefined && bays.bayAt(s.fx[slot], s.fy[slot], s.fz[slot], bay) ? bay.frame : null
  undockFrom[o] = frame !== null ? bay.pos.x : s.fx[slot]
  undockFrom[o + 1] = frame !== null ? bay.pos.y : s.fy[slot]
  undockFrom[o + 2] = frame !== null ? bay.pos.z : s.fz[slot]
  const axis = undockM < 0 ? frame : null
  undockAxis[o] = axis !== null ? axis.out.x : 0
  undockAxis[o + 1] = axis !== null ? axis.out.y : 0
  undockAxis[o + 2] = axis !== null ? axis.out.z : 0
  undockMouth[o] = axis !== null ? axis.mouth.x : 0
  undockMouth[o + 1] = axis !== null ? axis.mouth.y : 0
  undockMouth[o + 2] = axis !== null ? axis.mouth.z : 0
  const clear = undockM >= 0 ? undockM : axis !== null ? bay.radius + UNDOCK_CLEAR_M : env.cfg.dockStartM
  undockClear[eid] = clear
  return clear
}

/** True when `def` moors instead of entering a station bay (its band's `dockMode` is `moor`: an L). Allocation-free. */
export function moorsAt(def: ShipDef): boolean {
  return dockModeOf(def.sizeBand) === 'moor'
}

/**
 * The mooring-ring slot an AI ship at contact `slot` takes: any slot but the player's (`playerSlot`), spread by the
 * contact slot. Allocation-free.
 */
export function aiRingSlot(slot: number, cfg: MoorConfig): number {
  const n = cfg.slots
  if (n <= 1) return 0
  return (cfg.playerSlot + 1 + (slot % (n - 1))) % n
}

/**
 * True when ship `eid` is out of the living player's view: beyond `despawnFarM`, or outside
 * `despawnViewHalfAngleDeg` of the player's nose (local −Z). No living player → true. Allocation-free.
 */
export function outOfPlayerView(sim: Sim, eid: number, cfg: MoorConfig): boolean {
  const p = sim.playerEid
  if (p < 0 || Life.alive[p] !== 1) return true
  const dx = Pos64.x[eid] - Pos64.x[p]
  const dy = Pos64.y[eid] - Pos64.y[p]
  const dz = Pos64.z[eid] - Pos64.z[p]
  const d2 = dx * dx + dy * dy + dz * dz
  if (d2 > cfg.despawnFarM * cfg.despawnFarM) return true
  const d = Math.sqrt(d2)
  if (d < 1e-6) return false
  rot.x = Rot.x[p]
  rot.y = Rot.y[p]
  rot.z = Rot.z[p]
  rot.w = Rot.w[p]
  rotateVec(rot, 0, 0, -1, nose)
  return (nose.x * dx + nose.y * dy + nose.z * dz) / d < Math.cos(cfg.despawnViewHalfAngleDeg * DEG)
}

/** `moorSpawnPoint`'s answer when a moor-band ship must stay a contact for now (its station's bay isn't ready). */
export const MOOR_WAIT = -2

/**
 * Where a moor-band ship promoted at contact `slot` spawns (M10 P1e3; rare, at promotion): when its leg starts at a
 * station (`bays.bayAt` on the leg's `from`) and the contact is still inside that station's mooring ring, writes its
 * AI ring point to `out` and returns the distance from the station the `undock` phase runs (clear of the ring by
 * `ringClearM`, at least `dockStartM`). When the leg starts at a station with no bay yet (its model still streaming
 * in: `bays.stationNear` but not `bayAt`) and the contact is inside that station's ring (`moorRingRadius(R)` round its
 * centre, both from `stationNear`'s `out`; without them `moorRingRadius(0)` round the leg's start; scale SC-5d2), returns
 * `MOOR_WAIT`: the ring is unknown, and spawning at the contact would put the L in the station and on every ship that
 * undocks there with it (the Mission 2 barge and its escort; M10 P3 W4b). Otherwise −1 (spawn at the contact).
 */
export function moorSpawnPoint(env: TrafficPilotEnv, slot: number, out: Vec3): number {
  const s = env.store
  const bays = env.bays
  if (s.kind[slot] !== STEP_KIND.leg || bays === null || bays.bayAt === undefined) return -1
  const m = env.moor ?? MOOR_TUNING
  if (!bays.bayAt(s.fx[slot], s.fy[slot], s.fz[slot], spawnBay) || spawnBay.frame === null) {
    if (bays.stationNear === undefined) return -1
    spawnBay.pos.x = s.fx[slot]
    spawnBay.pos.y = s.fy[slot]
    spawnBay.pos.z = s.fz[slot]
    spawnBay.radius = 0
    if (!bays.stationNear(s.fx[slot], s.fy[slot], s.fz[slot], moorRingRadius(0, m), spawnBay)) return -1
    const r0 = moorRingRadius(spawnBay.radius, m)
    const ex = s.x[slot] - spawnBay.pos.x
    const ey = s.y[slot] - spawnBay.pos.y
    const ez = s.z[slot] - spawnBay.pos.z
    return ex * ex + ey * ey + ez * ez < r0 * r0 ? MOOR_WAIT : -1
  }
  const r = moorRingRadius(spawnBay.radius, m)
  const dx = s.x[slot] - spawnBay.pos.x
  const dy = s.y[slot] - spawnBay.pos.y
  const dz = s.z[slot] - spawnBay.pos.z
  if (dx * dx + dy * dy + dz * dz >= r * r) return -1
  moorRingPoint(spawnBay.pos, spawnBay.radius, spawnBay.frame.out, aiRingSlot(slot, m), m, out)
  const clear = r + m.ringClearM
  return clear > env.cfg.dockStartM ? clear : env.cfg.dockStartM
}

/** Adds or removes the M1 `AIPilot` on threat (spec §10.3). Returns true while the AI pilot flies the ship. */
function stepThreat(env: TrafficPilotEnv, sim: Sim, eid: number, slot: number): boolean {
  const cfg = env.cfg
  const s = env.store
  const health = Hull.value[eid] + Shield.value[eid]
  const hit = health < lastHealth[eid] - 1e-3
  lastHealth[eid] = health
  const team = Team.id[eid]
  const ships = sim.ships.dense
  const threat2 = cfg.threatM * cfg.threatM
  const calm2 = cfg.calmM * cfg.calmM
  let nearest2 = Infinity
  for (let i = 0; i < ships.length; i++) {
    const o = ships[i]
    if (o === eid || Life.alive[o] !== 1 || !isHostile(team, Team.id[o])) continue
    const dx = Pos64.x[o] - Pos64.x[eid]
    const dy = Pos64.y[o] - Pos64.y[eid]
    const dz = Pos64.z[o] - Pos64.z[eid]
    const d2 = dx * dx + dy * dy + dz * dz
    if (d2 < nearest2) nearest2 = d2
  }
  const piloted = hasComponent(sim.world, eid, AIPilot)
  if (hit || nearest2 < (piloted ? calm2 : threat2)) s.combatAt[slot] = sim.time
  if (!piloted) {
    if (!(hit || nearest2 < threat2)) return false
    const list = cfg.aiProfiles[s.role[slot]]
    const id = list !== undefined && list.length > 0 ? list[eid % list.length] : 'rookie'
    const profile = env.profileIndex(id)
    addComponent(sim.world, eid, AIPilot)
    AIPilot.state[eid] = 0
    AIPilot.profile[eid] = profile >= 0 ? profile : 0
    AIPilot.target[eid] = -1
    AIPilot.stateTimer[eid] = 0
    AIPilot.jinkX[eid] = AIPilot.jinkY[eid] = 0
    AIPilot.sampleTimer[eid] = 0
    AIPilot.lastAttacker[eid] = -1
    // The promoted ship's def band (M10 P4b5d): an L pilot reads the L stand-off tuning. Civil L keep their role's
    // profile (civilian: they flee).
    setPilotBand(eid, bandRank(sim.defs[Ship.defIndex[eid]].sizeBand))
    ToolControl.fire[eid] = 0
    return true
  }
  if (sim.time - s.combatAt[slot] < cfg.calmS) return true
  removeComponent(sim.world, eid, AIPilot)
  AIPilot.target[eid] = -1 // a crewed battery reads it as the ship's target (M10 P5a7b, review M1)
  setPilotBand(eid, 0)
  Ship.faOn[eid] = 1
  idle(eid)
  return false
}

function dockStateOf(eid: number): AutodockState {
  let st = dockStates[eid]
  if (st === undefined) {
    st = createAutodockState()
    dockStates[eid] = st
  }
  return st
}

/** Runs the dock phase (spec §10.3). */
function stepDock(env: TrafficPilotEnv, eid: number, slot: number, def: ShipDef): void {
  const s = env.store
  const docking = env.docking
  if (env.bays === null || docking === null || !env.bays.bayOf(s.targetId[slot], bay) || bay.frame === null) {
    if (!warnedNoBays) {
      warnedNoBays = true
      logger.warn('traffic: no docking bay for AI traffic; ships dock on reaching the station')
    }
    TrafficPilot.phase[eid] = TRAFFIC_PHASE.done
    idle(eid)
    return
  }
  shipPos.x = Pos64.x[eid]
  shipPos.y = Pos64.y[eid]
  shipPos.z = Pos64.z[eid]
  shipVel.x = Vel.x[eid]
  shipVel.y = Vel.y[eid]
  shipVel.z = Vel.z[eid]
  shipRot.x = Rot.x[eid]
  shipRot.y = Rot.y[eid]
  shipRot.z = Rot.z[eid]
  shipRot.w = Rot.w[eid]
  Ship.faOn[eid] = 1
  // The bay scratch is the station (its pos and radius): objects only into the un-inlined step (DA-1 follow-up, D-TBD).
  const r = stepAutodockAt(dockStateOf(eid), bay.frame, bay, dockShip, def, docking, stageDt, dockOut)
  Control.surge[eid] = dockOut.surge
  Control.sway[eid] = dockOut.sway
  Control.heave[eid] = dockOut.heave
  Control.pitch[eid] = dockOut.pitch
  Control.yaw[eid] = dockOut.yaw
  Control.roll[eid] = dockOut.roll
  Control.boost[eid] = 0
  if (r === 'arrived') {
    TrafficPilot.phase[eid] = TRAFFIC_PHASE.done
  } else if (r === 'failed') {
    if (dockRetries[eid]++ < 1) {
      resetAutodock(dockStateOf(eid))
    } else {
      logger.warn(`traffic: ${s.ids[slot]} could not dock at ${s.targetId[slot]}; it docks anyway`)
      TrafficPilot.phase[eid] = TRAFFIC_PHASE.done
    }
  }
}

/**
 * Runs the dock phase of a moor-band ship (M10 P1e3): flies to its AI ring slot round the target station, holds
 * there (nose along the ring tangent) for `aiDwellS`, then `done` once out of the player's view. Without a bay the
 * ship holds where it is (logged once) under the same out-of-view rule. Allocation-free.
 */
function stepMoor(env: TrafficPilotEnv, sim: Sim, eid: number, slot: number, def: ShipDef): void {
  const m = env.moor ?? MOOR_TUNING
  Ship.faOn[eid] = 1
  if (env.bays !== null && env.bays.bayOf(env.store.targetId[slot], bay) && bay.frame !== null) {
    moorRingPoint(bay.pos, bay.radius, bay.frame.out, moorSlot[eid], m, ring)
    const near = distTo(eid, ring.x, ring.y, ring.z) <= m.holdToleranceM
    flyTo(sim, eid, def, aim(ring.x, ring.y, ring.z, near ? m.holdSpeedMps : m.approachSpeedMps))
    if (!near) return
    moorRingTangent(bay.frame.out, moorSlot[eid], m, nose)
    const steer = sim.data.aiProfiles.tuning.steering
    steerDir(nose.x, nose.y, nose.z, steer.turnGain, steer.rollGain, eid)
  } else {
    if (!warnedNoRing) {
      warnedNoRing = true
      logger.warn('traffic: no station bay for a mooring ring; moor-band ships hold where they are')
    }
    idle(eid)
  }
  moorHeld[eid] += stageDt
  if (moorHeld[eid] >= m.aiDwellS && outOfPlayerView(sim, eid, m)) TrafficPilot.phase[eid] = TRAFFIC_PHASE.done
}

/**
 * The target station's bounding radius for ship `eid` (scale SC-5d2): cached by `beginTrafficPilot`; while unknown (its
 * bay not ready then), looked up again inside `approachDropM` of the centre (`d`). Allocation-free.
 */
function targetRadius(env: TrafficPilotEnv, eid: number, slot: number, d: number): number {
  if (targetR[eid] === 0 && d < env.cfg.approachDropM && env.bays !== null && env.bays.bayOf(env.store.targetId[slot], bay)) targetR[eid] = bay.radius
  return targetR[eid]
}

/** One traffic pilot step for real ship `eid` at `slot`. Allocation-free. */
export function stepTrafficPilot(env: TrafficPilotEnv, sim: Sim, eid: number, slot: number): void {
  const dt = stageDt
  if (Life.alive[eid] !== 1) return
  if (stepThreat(env, sim, eid, slot)) return
  const s = env.store
  const cfg = env.cfg
  const def = sim.defs[Ship.defIndex[eid]]
  const tx = TrafficPilot.tx[eid]
  const ty = TrafficPilot.ty[eid]
  const tz = TrafficPilot.tz[eid]
  TrafficPilot.timer[eid] += dt
  const sublight = cfg.sublightFraction * def.maxSpeed
  switch (TrafficPilot.phase[eid]) {
    case TRAFFIC_PHASE.undock: {
      // At the launch speed until undockClear from undockFrom (both set by beginTrafficPilot): along the bay's out
      // axis, aiming dockStartM ahead of the ship's foot on the axis line through the mouth (on the axis, never braking);
      // with no axis (a mooring ring, or no bay known), radially out from undockFrom.
      const o = eid * 3
      const fx = undockFrom[o]
      const fy = undockFrom[o + 1]
      const fz = undockFrom[o + 2]
      const d = distTo(eid, fx, fy, fz)
      if (d >= undockClear[eid]) {
        TrafficPilot.phase[eid] = TRAFFIC_PHASE.sublight
        break
      }
      const ax = undockAxis[o]
      const ay = undockAxis[o + 1]
      const az = undockAxis[o + 2]
      if (ax !== 0 || ay !== 0 || az !== 0) {
        const mx = undockMouth[o]
        const my = undockMouth[o + 1]
        const mz = undockMouth[o + 2]
        const a = (Pos64.x[eid] - mx) * ax + (Pos64.y[eid] - my) * ay + (Pos64.z[eid] - mz) * az + cfg.dockStartM
        flyTo(sim, eid, def, aim(mx + ax * a, my + ay * a, mz + az * a, cfg.undockSpeed))
        break
      }
      const k = d > 1e-3 ? cfg.dockStartM / d : 0
      const ox = Pos64.x[eid] + (Pos64.x[eid] - fx) * k
      const oy = Pos64.y[eid] + (Pos64.y[eid] - fy) * k + (k === 0 ? cfg.dockStartM : 0)
      const oz = Pos64.z[eid] + (Pos64.z[eid] - fz) * k
      flyTo(sim, eid, def, aim(ox, oy, oz, cfg.undockSpeed))
      break
    }
    case TRAFFIC_PHASE.sublight: {
      flyTo(sim, eid, def, aim(tx, ty, tz, sublight))
      const d = fly.dist
      if (s.kind[slot] !== STEP_KIND.leg || s.reported[slot] === 1) break
      const fromD = distTo(eid, s.fx[slot], s.fy[slot], s.fz[slot])
      if (fromD > cfg.cruiseClearM && d > cfg.approachDropM) {
        TrafficPilot.phase[eid] = TRAFFIC_PHASE.cruise
      } else if (s.targetKind[slot] === TARGET_KIND.station && d < targetRadius(env, eid, slot, d) + cfg.dockStartM) {
        TrafficPilot.phase[eid] = TRAFFIC_PHASE.dock
        TrafficPilot.timer[eid] = 0
        dockRetries[eid] = 0
        moorSlot[eid] = aiRingSlot(slot, env.moor ?? MOOR_TUNING)
        moorHeld[eid] = 0
        resetAutodock(dockStateOf(eid))
      } else if (s.targetKind[slot] !== TARGET_KIND.station && d < cfg.holdOrbitM * ARRIVE_FRACTION) {
        env.arrived(slot)
        TrafficPilot.phase[eid] = TRAFFIC_PHASE.hold
      }
      break
    }
    case TRAFFIC_PHASE.dock:
      if (moorsAt(def)) stepMoor(env, sim, eid, slot, def)
      else stepDock(env, eid, slot, def)
      break
    case TRAFFIC_PHASE.mine: {
      // Hold the standoff on the line from the field node to the ship, nose on the node, beam on.
      const d = distTo(eid, tx, ty, tz)
      const k = d > 1e-3 ? cfg.mineStandoffM / d : 0
      const px = tx + (Pos64.x[eid] - tx) * k
      const py = ty + (Pos64.y[eid] - ty) * k + (k === 0 ? cfg.mineStandoffM : 0)
      const pz = tz + (Pos64.z[eid] - tz) * k
      flyTo(sim, eid, def, aim(px, py, pz, sublight))
      const steer = sim.data.aiProfiles.tuning.steering
      steerDir(tx - Pos64.x[eid], ty - Pos64.y[eid], tz - Pos64.z[eid], steer.turnGain, steer.rollGain, eid)
      const inRange = d < cfg.mineStandoffM * 2
      ToolControl.mode[eid] = TOOL.mining
      ToolControl.fire[eid] = inRange ? 1 : 0
      ToolControl.power[eid] = inRange ? cfg.minePower : 0
      break
    }
    case TRAFFIC_PHASE.hold: {
      // Orbit the node in its horizontal plane, steering a little ahead of the ship's bearing; off the hull at a station.
      // A shown stand-by ship (RR-F1 BL-1b) holds still at its stand-by point (its target): an orbit would cross the
      // station. BL-R2: that point lies outside this hold orbit (R + holdOrbitM + standbyClearM, `standbyPoint`).
      const r = s.standby[slot] === 1 ? 0 : targetR[eid] + cfg.holdOrbitM
      const a = Math.atan2(Pos64.z[eid] - tz, Pos64.x[eid] - tx) + ORBIT_LEAD
      flyTo(sim, eid, def, aim(tx + Math.cos(a) * r, ty, tz + Math.sin(a) * r, sublight))
      break
    }
    default:
      idle(eid)
  }
}

/**
 * The `control` stage function over the store's real ships (register once per sim with
 * `registerSimStage(sim, 'control', createTrafficPilotStage(env))`). Allocation-free per tick.
 */
export function createTrafficPilotStage(env: TrafficPilotEnv, dt: number): SimStageFn {
  stageDt = dt
  return (sim: Sim): void => {
    const s = env.store
    for (let i = 0; i < s.count; i++) {
      const eid = s.eid[i]
      if (eid >= 0) stepTrafficPilot(env, sim, eid, i)
    }
  }
}
